import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {inject} from '@angular/core';
import {Logger} from '@ccp/lib/logger';
import {NotificationService} from '@ccp/services/notifications/notification.service';
import {NotificationTypes} from '@ccp/enums/notification-types.enum';
import {Observable, of} from 'rxjs';
import {Views} from '@ccp/config/views';
import {WebStorage} from '@ccp/lib/web-storage';
import {DeviceDetectorService} from 'ngx-device-detector';
import {UrlManager} from '@ccp/lib/url-manager';
import {Streams} from '@ccp/app/enums/streams.enums';
import {UrlParamLiterals} from '@ccp/enums/literals/urlparams.enum';
import {WSLiterals} from '@ccp/enums/literals/webstorage.enum';
import {NotificationMessage} from '@ccp/localization/notifications/notification-message';
import {PosSystemId} from '@ccp/enums/pos-systemid.enum';
import {ToggleFeatures} from '@ccp/enums/toggleFeatures.enum';
import {FeatureToggleService} from '@ccp/services/toggle-features/feature-toggle.service';
import {LocaleContextService} from '@ccp/services/app-context/locale-context.service';
import {UserContextService} from '@ccp/services/app-context/user-context.service';
import {ProductContextService} from '@ccp/services/app-context/product-context.service';

let localeContextService: LocaleContextService;
let userContextService: UserContextService;
let productContextService: ProductContextService;
let router;
let deviceService: DeviceDetectorService;
let notificationService: NotificationService;
let featureToggleService: FeatureToggleService;

export const SimpleRegistrationGuard: CanActivateFn = (route, state) => {
  router = inject(Router);
  localeContextService = inject(LocaleContextService);
  userContextService = inject(UserContextService);
  productContextService = inject(ProductContextService);
  deviceService = inject(DeviceDetectorService);
  notificationService = inject(NotificationService);
  featureToggleService = inject(FeatureToggleService);

  // if user is on:
  // - complete information in china while logged without a phone number
  // - complete information in rotw while not logged and with params ok
  // - simplified register in china while not logged and with params ok
  // then we allow him go trough.
  if (isAllowedFlow(state)) {
    WebStorage.set(WSLiterals.simpleRegFlow, getFlow(state));
    return of(true);
  }

  // otherwise, we deploy our more complex behavior...
  return handleUnauthorizedCases(route, state);
};

function isAllowedFlow(state: RouterStateSnapshot): boolean {
  return isGemini(state) || isPOS_B(state) || isPOS_E(state) || isPOS_NextGenCs(state);
}

function handleUnauthorizedCases(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
  // in china, if user goes anywhere while logged without a phone number, then
  // he is redirected to complete information
  if (isOutsideCompleteInfo(state)) {
    return handleOutsideCompleteInfo(route, state);
  }

  // in china, if user tries to go to complete information while logged with a
  // phone or not logged, we redirect him to portfolio or login
  if (isUnauthorizedGemini(state)) {
    return handleUnauthorizedGemini(route, state);
  }

  // we continue the extension flow
  if (isPOS_BLogged(state)) {
    continuePOS_BFlow();
    return of(false);
  }
  // in rotw, if user tries to go to complete information
  // without the proper url parameters, we redirect him to the homepage
  if (isUnauthorizedPOS_B(state)) {
    handleUnauthorizedPOS_B(route);
    return of(false);
  }

  // if user tries to go to simplified registration while logged or without
  // the proper parameters or while in rotw, we redirect him either
  // to registration or register page.
  if (isUnauthorizedPOS_E(state)) {
    return handleUnauthorizedPOS_E(route);
  }

  return of(true);
}

function isCompleteInfoPath(state: RouterStateSnapshot): boolean {
  return UrlManager.eq(state.url, Views.completeAccount.path);
}

function isOutsideCompleteInfo(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();
  return localeContextService.currentCountry$().isChina && user.isLogged && !isCompleteInfoPath(state) && !user.phoneNumber;
}

function handleOutsideCompleteInfo(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
  const user = userContextService.currentUser$();
  Logger.error('Gemini: Mandatory phone in china when logged!', {
    messageContext: {
      'isChina (true)': localeContextService.currentCountry$().isChina.toString(),
      'isLogged (true)': user.isLogged.toString(),
      'isCompleteInfoPath (false)': isCompleteInfoPath(state).toString(),
      'phoneNumber (false)': user.phoneNumber,
      'Redirecting to': Views.completeAccount.path,
    },
  });
  redirectToCompleteInfo(route, state);
  return of(false);
}

function getFlow(state: RouterStateSnapshot): Streams {
  if (isGemini(state)) {
    return Streams.GEMINI;
  }
  if (isPOS_NextGenCs(state)) {
    return Streams.NEXT_GEN_CS;
  }
  if (isPOS_B(state)) {
    return Streams.POS_B;
  }
  if (isPOS_E(state)) {
    return Streams.POS_E;
  }
}

/************************* GEMINI *********************************/
function isGemini(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();
  return isCompleteInfoPath(state) && localeContextService.currentCountry$().isChina && user.isLogged && !user.phoneNumber;
}

function isUnauthorizedGemini(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();
  return isCompleteInfoPath(state) && localeContextService.currentCountry$().isChina && (!user.isLogged || user.phoneNumber.isEmpty);
}

function handleUnauthorizedGemini(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
  const user = userContextService.currentUser$();
  Logger.error('Complete information unauthorized', {
    messageContext: {
      'isChina (true)': localeContextService.currentCountry$().isChina.toString(),
      'isLogged (false)': user.isLogged.toString(),
      'isCompleteInfoPath (true)': isCompleteInfoPath(state).toString(),
      'phoneNumber (false)': user.phoneNumber,
      result: 'Redirecting...',
    },
  });

  if (!user.isLogged) {
    redirectToLogin(route);
    return of(false);
  }
  // dead code?
  redirectToPortfolio(route);
  return of(false);
}

/************************* POS_B *********************************/
function isPOS_B(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();

  return isCompleteInfoPath(state) && !localeContextService.currentCountry$().isChina && !user.isLogged && arePOS_B_paramsOK();
}

function isPOS_BLogged(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();
  return (
    isCompleteInfoPath(state) &&
    !localeContextService.currentCountry$().isChina &&
    user.isLogged &&
    (arePOS_B_paramsOK() ||
      (arePOS_B_WithoutToken_paramsOK() && !!featureToggleService.featureConfig$()?.getConfig(ToggleFeatures.hydraSso)))
  );
}

function arePOS_B_paramsOK(): boolean {
  const params = UrlManager.getParams();
  const token = params[UrlParamLiterals.Token] ?? '';
  const websiteCountry = params[UrlParamLiterals.WebsiteCountry] ?? '';
  return [token, websiteCountry].noEmpty();
}

function arePOS_B_WithoutToken_paramsOK(): boolean {
  const params = UrlManager.getParams();
  const websiteCountry = params[UrlParamLiterals.WebsiteCountry] ?? '';
  return [websiteCountry].noEmpty();
}

function continuePOS_BFlow(): void {
  WebStorage.set(WSLiterals.simpleRegFlow, Streams.POS_B);
  const params = UrlManager.getParams();
  const POSBQueryParams = {
    [UrlParamLiterals.From]: Views.completeAccount.path,
    [UrlParamLiterals.WarrantyUid]: params[UrlParamLiterals.WarrantyUid] ?? '',
    [UrlParamLiterals.SystemId]: params[UrlParamLiterals.SystemId] ?? '',
    [UrlParamLiterals.PosRequestId]: params[UrlParamLiterals.PosRequestId] ?? '',
    [UrlParamLiterals.RepairNumber]: params[UrlParamLiterals.RepairNumber] ?? '',
  };
  const redirPath = Views.productRequestStatus.path + UrlManager.paramsToQueryString(POSBQueryParams, false);
  router.navigateByUrl(redirPath);
}

function isUnauthorizedPOS_B(state: RouterStateSnapshot): boolean {
  return isCompleteInfoPath(state) && !isPOS_B(state);
}

function handleUnauthorizedPOS_B(route: ActivatedRouteSnapshot): UrlTree {
  const user = userContextService.currentUser$();
  Logger.error('POS B unauthorized!', {
    messageContext: {
      isRotw: (!localeContextService.currentCountry$().isChina).toString(),
      isNotLogged: (!user.isLogged).toString(),
      POS_B_ParamsOK: arePOS_B_paramsOK().toString(),
      condition: '(All of these should be true)',
    },
  });
  return redirectToHome(route);
}

/************************* POS_E *********************************/
function isPOS_E(state: RouterStateSnapshot): boolean {
  const user = userContextService.currentUser$();
  return (
    UrlManager.eq(state.url, Views.simplifiedRegistration.path) &&
    localeContextService.currentCountry$().isChina &&
    !user.isLogged &&
    arePOS_E_paramsOK()
  );
}

function isUnauthorizedPOS_E(state: RouterStateSnapshot): boolean {
  return UrlManager.eq(state.url, Views.simplifiedRegistration.path) && !isPOS_E(state);
}

function handleUnauthorizedPOS_E(route: ActivatedRouteSnapshot): Observable<boolean> {
  const user = userContextService.currentUser$();

  Logger.error('POS E unauthorized!', {
    messageContext: {
      isChina: localeContextService.currentCountry$().isChina.toString(),
      isNotLogged: (!user.isLogged).toString(),
      POS_E_ParamsOk: arePOS_E_paramsOK().toString(),
      condition: '(All of these should be true)',
    },
  });

  if (!localeContextService.currentCountry$().isChina || !user.isLogged) {
    redirectToRegister(route);
    return of(false);
  }

  if (!arePOS_E_paramsOK()) {
    return POS_E_WrongParamRedirect();
  }
}

function POS_E_WrongParamRedirect(): Observable<boolean> {
  const isMobile = deviceService.isMobile();
  // CCP-10771 : If is mobile, create account, otherwise, register product.
  const to = isMobile ? Views.userRegister.path : Views.productRegister.path;
  router.navigate([to]).then(() => {
    notificationService.send(NotificationMessage['USER_SIMPLIFIED_REGISTRATION_ERROR'], NotificationTypes.Error);
  });
  // need to remove registering product manually because we are redirected into an extend warranty flow page
  productContextService.removeRegisteredProduct();
  return of(false);
}

function arePOS_E_paramsOK(): boolean {
  const params = UrlManager.getParams();
  const posRequestId = params[UrlParamLiterals.PosRequestId] ?? '';
  const hash = params[UrlParamLiterals.Hash] ?? '';
  return [posRequestId, hash].noEmpty();
}

/************************* NEXTGEN CS *********************************/
function isPOS_NextGenCs(state: RouterStateSnapshot): boolean {
  const systemId = UrlManager.getParams()[UrlParamLiterals.SystemId] || '';
  return isPOS_B(state) && systemId === PosSystemId.NextGenCs;
}

/************************* REDIRECT *********************************/
function redirectToCompleteInfo(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree {
  WebStorage.set(WSLiterals.ccpRedirectUrl, state.url);
  WebStorage.set(WSLiterals.simpleRegFlow, getFlow(state));
  return redirect(route, Views.completeAccount.path);
}

function redirectToHome(route: ActivatedRouteSnapshot): UrlTree {
  return redirect(route, Views.productRegister.path);
}

function redirectToRegister(route: ActivatedRouteSnapshot): UrlTree {
  return redirect(route, Views.userRegister.path);
}

function redirectToPortfolio(route: ActivatedRouteSnapshot): UrlTree {
  return redirect(route, Views.portfolio.path);
}

function redirectToLogin(route: ActivatedRouteSnapshot): UrlTree {
  return redirect(route, Views.userLogin.path);
}

function redirect(route: ActivatedRouteSnapshot, to: string): UrlTree {
  let qp = {...route.queryParams};
  const params = UrlManager.getParams();
  const systemId = params?.[UrlParamLiterals.SystemId] || '';
  if (systemId) {
    qp = {...route.queryParams, [UrlParamLiterals.SystemId]: systemId};
  }

  return router.createUrlTree([to], {queryParams: qp});
}
